import type { ChipColor } from '@/types/chip';
import type { ServiceAuthorisationRecord } from '@/types/entities';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, Chip, TablePagination, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { VerifiedUser } from '@mui/icons-material';
import { useGetList } from 'react-admin';
import { useState } from 'react';
import { ListPageHeader } from '@/components/ListPageHeader';

interface TutorItem {
  id: string;
  legalEntity?: { firstName?: string; lastName?: string };
}

interface ServiceItem {
  id: string;
  name?: string;
}

const STATUSES = ['Pending', 'Active', 'Expired', 'Revoked'];

const STATUS_COLORS: Record<string, ChipColor> = {
  Pending: 'warning',
  Active: 'success',
  Expired: 'error',
  Revoked: 'default',
};

export const ServiceAuthorisationList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState<Record<string, string | undefined>>({});
  const { data: allAuthorisations } = useGetList('service-authorisations', { pagination: { page: 1, perPage: 1000 }, filter: filters });
  const { data: allTutors } = useGetList('tutors', { pagination: { page: 1, perPage: 1000 } });
  const { data: allServices } = useGetList('services', { pagination: { page: 1, perPage: 1000 } });

  const authorisations = allAuthorisations?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const tutorMap = new Map((allTutors ?? []).map((t: TutorItem) => [t.id, t]));
  const serviceMap = new Map((allServices ?? []).map((s: ServiceItem) => [s.id, s]));

  const resolveTutorName = (id?: string) => {
    if (!id) return '—';
    const tutor = tutorMap.get(id);
    const first = tutor?.legalEntity?.firstName ?? '';
    const last = tutor?.legalEntity?.lastName ?? '';
    const name = `${first} ${last}`.trim();
    return name || id;
  };

  const resolveServiceName = (id?: string) => {
    if (!id) return '—';
    const service = serviceMap.get(id);
    return service?.name || id;
  };

  const stats = {
    total: allAuthorisations?.length ?? 0,
    active: allAuthorisations?.filter((a: ServiceAuthorisationRecord) => a.status === 'Active').length ?? 0,
    pending: allAuthorisations?.filter((a: ServiceAuthorisationRecord) => a.status === 'Pending').length ?? 0,
    expired: allAuthorisations?.filter((a: ServiceAuthorisationRecord) => a.status === 'Expired').length ?? 0,
    revoked: allAuthorisations?.filter((a: ServiceAuthorisationRecord) => a.status === 'Revoked').length ?? 0,
  };

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="Service Authorisations"
        metrics={[
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
          { label: 'Active', value: String(stats.active), color: 'success.main' },
          { label: 'Pending', value: String(stats.pending), color: 'warning.main' },
          { label: 'Expired', value: String(stats.expired), color: 'error.main' },
          { label: 'Revoked', value: String(stats.revoked), color: 'grey.600' },
        ]}
      />
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status || ''}
            label="Status"
            onChange={e => setFilters(prev => ({ ...prev, status: e.target.value || undefined }))}
          >
            <MenuItem value="">All</MenuItem>
            {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Tutor</InputLabel>
          <Select
            value={filters.tutorId || ''}
            label="Tutor"
            onChange={e => setFilters(prev => ({ ...prev, tutorId: e.target.value || undefined }))}
          >
            <MenuItem value="">All</MenuItem>
            {allTutors?.map((t: TutorItem) => <MenuItem key={t.id} value={t.id}>{t.legalEntity?.firstName ?? ""} {t.legalEntity?.lastName ?? ""}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      <Paper sx={{ overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Tutor</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Service</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Valid From</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Valid Until</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {authorisations?.map((auth: ServiceAuthorisationRecord) => (
              <TableRow
                key={auth.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/service-authorisations/${auth.id}/show`)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <VerifiedUser sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={500}>{resolveTutorName(auth.tutor?.id)}</Typography>
                  </Box>
                </TableCell>
                <TableCell><Typography variant="body2">{resolveServiceName(auth.service?.id)}</Typography></TableCell>
                <TableCell><Typography variant="body2">{auth.effectiveFrom || '—'}</Typography></TableCell>
                <TableCell><Typography variant="body2">{auth.effectiveTo || '—'}</Typography></TableCell>
                <TableCell><Chip label={auth.status} size="small" color={STATUS_COLORS[auth.status]} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={allAuthorisations?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!authorisations || authorisations.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <VerifiedUser sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No service authorisations found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
